import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FileText, Building2, Shield, AlertCircle, BookOpen, ArrowRight, CheckCircle2, Calculator } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
};
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };

/* @section: services */
export default function Services() {
  const { t } = useLanguage();

  const serviceGroups = [
    {
      icon: <FileText className="w-6 h-6" />,
      titleEn: "Individual & Business Tax Returns",
      titleKo: "개인 & 비즈니스 세금보고",
      to: "/individual-tax",
      items: [
        { en: "Individual Tax Return Preparation", ko: "개인 세금보고" },
        { en: "Small Business Tax Return Preparation", ko: "스몰비즈니스 세금보고" },
        { en: "LLC, S-Corp, Partnership Tax Filing", ko: "LLC, S-Corp, Partnership 세금보고" },
        { en: "W-2, 1099, Schedule C, E Returns", ko: "W-2, 1099, Schedule C, E 신고" },
        { en: "Stock, Crypto, RSU/ESPP Reporting", ko: "주식, 가상화폐, RSU/ESPP 보고" },
        { en: "Rental Property Income Reporting", ko: "임대 소득 신고" },
      ],
    },
    {
      icon: <Shield className="w-6 h-6" />,
      titleEn: "Small Business Tax Planning",
      titleKo: "스몰비즈니스 절세 플랜",
      to: "/tax-planning",
      items: [
        { en: "Tax Planning & Strategy Review", ko: "절세 플랜 및 전략 검토" },
        { en: "Estimated Tax Planning", ko: "예상세금 계획" },
        { en: "Entity Structure Review (LLC vs S-Corp)", ko: "사업 구조 검토 (LLC vs S-Corp)" },
        { en: "Reasonable Salary & S-Corp Planning", ko: "합리적 급여 및 S-Corp 계획" },
        { en: "Retirement Plan Options", ko: "은퇴 플랜 옵션 검토" },
        { en: "Year-End Tax Projection", ko: "연말 세금 예측" },
      ],
    },
    {
      icon: <Building2 className="w-6 h-6" />,
      titleEn: "Business Formation Support",
      titleKo: "회사 설립 지원",
      to: "/business-formation",
      items: [
        { en: "LLC Formation Guidance", ko: "LLC 설립 지원" },
        { en: "S-Corp Election Review", ko: "S-Corp 선택 검토" },
        { en: "EIN Application Support", ko: "EIN 신청 지원" },
        { en: "FBN / DBA Guidance", ko: "FBN / DBA 안내" },
        { en: "First-Year Tax Setup", ko: "첫 해 세금 셋업" },
        { en: "Basic Compliance Checklist", ko: "기본 컴플라이언스 체크리스트" },
      ],
    },
    {
      icon: <AlertCircle className="w-6 h-6" />,
      titleEn: "IRS Notices & Tax Problem Support",
      titleKo: "IRS 통지서 & 세금 문제 대응",
      to: "/irs-notices",
      items: [
        { en: "IRS Notice Review & Response Preparation", ko: "IRS 통지서 검토 및 답변 준비" },
        { en: "Balance Due Explanation", ko: "미납세액 설명" },
        { en: "Penalty Notice Review", ko: "가산세 통지서 검토" },
        { en: "IRS Account Transcript Review", ko: "IRS 계정 기록 검토" },
        { en: "Enrolled Agent Representation", ko: "Enrolled Agent 대리인 서비스" },
        { en: "State Tax Notice Response", ko: "주정부 세금 통지서 대응" },
      ],
    },
    {
      icon: <BookOpen className="w-6 h-6" />,
      titleEn: "Bookkeeping & Tax Readiness",
      titleKo: "장부 검토 & 세금보고 준비",
      to: "/services",
      items: [
        { en: "Bookkeeping Review for Tax Readiness", ko: "세금보고 전 장부 리뷰" },
        { en: "QuickBooks Clean-Up", ko: "QuickBooks 정리" },
        { en: "Business Expense Classification Review", ko: "사업 비용 분류 검토" },
        { en: "Deduction Documentation Guidance", ko: "공제 항목 서류 안내" },
        { en: "Accountable Plan Review", ko: "Accountable Plan 검토" },
      ],
    },
    {
      icon: <Calculator className="w-6 h-6" />,
      titleEn: "Payroll Services",
      titleKo: "페이롤 서비스",
      to: "/services",
      items: [
        { en: "Payroll Setup for Small Business", ko: "스몰비즈니스 페이롤 셋업" },
        { en: "Regular Payroll Processing", ko: "정기 급여 처리" },
        { en: "Payroll Tax Calculation & Deposits", ko: "급여세 계산 및 납부" },
        { en: "W-2 & 1099 Year-End Filings", ko: "W-2 & 1099 연말 신고" },
        { en: "Payroll Compliance Review", ko: "페이롤 컴플라이언스 검토" },
        { en: "S-Corp Reasonable Salary & Payroll Coordination", ko: "S-Corp 합리적 급여 및 페이롤 조율" },
      ],
    },
  ];

  return (
    <>
      {/* Page Header */}
      <section className="bg-primary py-16 md:py-20 px-4">
        <div className="container mx-auto max-w-4xl text-center">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.p variants={fadeUp} className="text-accent text-sm font-semibold uppercase tracking-wider mb-3">
              {t("Our Services", "서비스 안내")}
            </motion.p>
            <motion.h1 variants={fadeUp} className="text-4xl md:text-5xl font-bold text-primary-foreground mb-4">
              {t("Comprehensive Tax Services", "종합 세무 서비스")}
            </motion.h1>
            <motion.p variants={fadeUp} className="text-primary-foreground/70 text-lg max-w-2xl mx-auto">
              {t(
                "From tax returns to year-round planning and IRS representation — we cover every aspect of your tax needs.",
                "세금보고부터 연중 절세 계획, IRS 대응까지 — 모든 세무 니즈를 담당합니다."
              )}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Services List */}
      <section className="py-16 md:py-24 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="space-y-10">
            {serviceGroups.map((group, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-60px" }}
                variants={fadeUp}
                className="bg-card border border-border rounded-2xl overflow-hidden"
              >
                <div className="flex items-center gap-4 p-6 border-b border-border bg-muted/50">
                  <div className="w-11 h-11 rounded-lg bg-primary flex items-center justify-center text-primary-foreground shrink-0">
                    {group.icon}
                  </div>
                  <h2 className="text-xl font-bold text-foreground">{t(group.titleEn, group.titleKo)}</h2>
                  <Link
                    to={group.to}
                    className="ml-auto inline-flex items-center gap-1.5 text-sm font-medium text-accent hover:underline whitespace-nowrap"
                  >
                    {t("Learn more", "더 알아보기")} <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {group.items.map((item, j) => (
                      <div key={j} className="flex items-start gap-2.5">
                        <CheckCircle2 className="w-4 h-4 text-accent mt-0.5 shrink-0" />
                        <span className="text-sm text-foreground">{t(item.en, item.ko)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* CTA */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="mt-14 text-center p-10 bg-primary rounded-2xl"
          >
            <h3 className="text-2xl font-bold text-primary-foreground mb-3">
              {t("Not sure which service you need?", "어떤 서비스가 필요한지 모르겠나요?")}
            </h3>
            <p className="text-primary-foreground/70 mb-6">
              {t(
                "Schedule a consultation and we'll help you figure out exactly what you need.",
                "상담을 예약하시면 필요한 서비스를 함께 정리해드립니다."
              )}
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-7 py-3 rounded-lg bg-accent text-white font-semibold hover:bg-accent/90 transition-colors shadow-md"
            >
              {t("Schedule a Consultation", "상담 예약하기")}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
}
